import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import generateAPIKeyAPI from "../../api/generateAPIKeyAPI";
import fetchAPIListAPI from "../../api/fetchAPIListAPI";
import deleteAPIKeyAPI from "../../api/deleteAPIKeyAPI";
import toggleAPIKeyStatusAPI from "../../api/toggleAPIKeyStatusAPI";

const ApiManagement = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [revealedTokens, setRevealedTokens] = useState({});
  const [loadingKeys, setLoadingKeys] = useState({}); // Track loading state for each API key
  const [generating, setGenerating] = useState(false);

  // Simulate fetching API keys from the backend
  useEffect(() => {
    fetchAPIs();
  }, []);

  const fetchAPIs = async () => {
    setIsLoading(true);
    try {
      const res = await fetchAPIListAPI();
      setApiKeys(res.data.apiKeys);
    } catch (error) {
      console.error(error);
      setSnackbarMessage("Failed to fetch API keys. Please try again.");
      setSnackbarOpen(true);
    }
    setIsLoading(false);
  };

  // Generate a mock API key
  const generateApiKey = async () => {
    if (!newApiKeyName.trim()) {
      setSnackbarMessage("Please enter a name for the API key.");
      setSnackbarOpen(true);
      return;
    }

    setGenerating(true);

    try {
      const res = await generateAPIKeyAPI({
        name: newApiKeyName,
      });

      setApiKeys([...apiKeys, res.data.apiKey]);
      setNewApiKeyName("");
      setShowGenerateDialog(false);
      setSnackbarMessage("API key generated successfully!");
    } catch (error) {
      console.error(error);
      if (error.response?.status === 400) {
        setSnackbarMessage(error.response.data.error);
      } else {
        setSnackbarMessage("Failed to generate API key. Please try again.");
      }
    }
    setSnackbarOpen(true);
    setGenerating(false);
  };

  // Toggle API key active state (simulate API call)
  const toggleApiKeyStatus = async (name) => {
    setLoadingKeys((prev) => ({ ...prev, [name]: true })); // Start loading

    try {
      const key = apiKeys.find((key) => key.name === name);
      const toggleResponse = await toggleAPIKeyStatusAPI({
        name,
        isActive: !key.isActive,
      });

      setApiKeys((prevKeys) =>
        prevKeys.map((key) =>
          key.name === name ? { ...key, isActive: !key.isActive } : key
        )
      );
    } catch (error) {
      console.error(error);
      setSnackbarMessage("Failed to toggle API key status. Please try again.");
      setSnackbarOpen(true);
    }

    setLoadingKeys((prev) => ({ ...prev, [name]: false })); // Stop loading
  };

  // Open delete confirmation dialog
  const openDeleteConfirmation = (name) => {
    setKeyToDelete(name);
    setDeleteConfirmationOpen(true);
  };

  // Delete an API key
  const confirmDelete = async () => {
    setLoadingKeys((prev) => ({ ...prev, [keyToDelete]: true })); // Start loading
    try {
      const deleted = await deleteAPIKeyAPI({
        name: keyToDelete,
      });
      setApiKeys((prevKeys) =>
        prevKeys.filter((key) => key.name !== keyToDelete)
      );
      setSnackbarMessage("API key deleted successfully!");
      setSnackbarOpen(true);
      setDeleteConfirmationOpen(false);
    } catch (error) {
      console.error(error);
      setSnackbarMessage("Failed to delete API key. Please try again.");
      setSnackbarOpen(true);
      setDeleteConfirmationOpen(false);
      return;
    }
    setLoadingKeys((prev) => ({ ...prev, [keyToDelete]: false })); // stop loading
  };

  // Close delete confirmation dialog
  const closeDeleteConfirmation = () => {
    setDeleteConfirmationOpen(false);
    setKeyToDelete(null);
  };

  // Toggle token visibility
  const toggleTokenVisibility = (token) => {
    setRevealedTokens((prev) => ({
      ...prev,
      [token]: !prev[token],
    }));
  };

  // Copy token to clipboard
  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    setSnackbarMessage("API key copied to clipboard!");
    setSnackbarOpen(true);
  };

  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ padding: 3, maxWidth: 800, margin: "0 auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "primary.main" }}
        >
          API Key Management
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            borderRadius: "8px",
            textTransform: "none",
            padding: "6px 16px",
            fontSize: "0.875rem",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
          onClick={() => setShowGenerateDialog(true)}
        >
          Generate API Key
        </Button>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : apiKeys.length === 0 ? (
        <Box sx={{ textAlign: "center", marginTop: 4 }}>
          <Typography variant="body1">No API keys found.</Typography>
        </Box>
      ) : (
        <>
          {apiKeys.map((key) => (
            <Card
              key={key.token}
              sx={{
                marginBottom: 2,
                boxShadow: 2,
                borderRadius: "8px",
                transition: "box-shadow 0.3s",
                "&:hover": {
                  boxShadow: 4,
                },
                position: "relative",
                opacity: loadingKeys[key.name] ? 0.7 : 1,
                pointerEvents: loadingKeys[key.name] ? "none" : "auto",
              }}
            >
              {loadingKeys[key.name] && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", marginBottom: 1 }}
                >
                  {key.name}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      color: "text.secondary",
                      wordBreak: "break-all",
                      flexGrow: 1,
                    }}
                  >
                    {revealedTokens[key.token] ? key.token : "•".repeat(16)}
                  </Typography>
                  <Tooltip
                    title={revealedTokens[key.token] ? "Hide" : "Reveal"}
                  >
                    <IconButton
                      onClick={() => toggleTokenVisibility(key.token)}
                      size="small"
                    >
                      {revealedTokens[key.token] ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy">
                    <IconButton
                      onClick={() => copyToken(key.token)}
                      size="small"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", gap: 1, marginTop: 2 }}>
                  <Button
                    variant="outlined"
                    color={key.isActive ? "error" : "primary"}
                    startIcon={<EditIcon />}
                    onClick={() => toggleApiKeyStatus(key.name)}
                    size="small"
                    sx={{ textTransform: "none" }}
                  >
                    {key.isActive ? "Disable" : "Enable"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => openDeleteConfirmation(key.name)}
                    size="small"
                    sx={{ textTransform: "none" }}
                  >
                    Delete
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </>
      )}

      {/* Generate API Key Dialog */}
      <Dialog
        open={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
        PaperProps={{
          sx: {
            width: "400px",
            borderRadius: "12px",
            padding: "16px",
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: "bold", fontSize: "1.25rem", paddingBottom: 0 }}
        >
          Generate API Key
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", marginBottom: 2 }}
          >
            Enter a name for your API key. This will help you identify it later.
          </Typography>
          <TextField
            fullWidth
            label="API Key Name"
            value={newApiKeyName}
            onChange={(e) => setNewApiKeyName(e.target.value)}
            margin="normal"
            size="small"
            sx={{ marginBottom: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowGenerateDialog(false)}
            size="small"
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={generateApiKey}
            color="primary"
            size="small"
            sx={{ textTransform: "none" }}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmationOpen}
        onClose={closeDeleteConfirmation}
        PaperProps={{
          sx: {
            width: "400px",
            borderRadius: "12px",
            padding: "16px",
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: "bold", fontSize: "1.25rem", paddingBottom: 0 }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Are you sure you want to delete this API key? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDeleteConfirmation}
            size="small"
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            size="small"
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={handleSnackbarClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default ApiManagement;
